import { useEffect, useState, type ReactNode } from 'react';
import { EventBus, useGameStore, useJwt, MAX_DESK_ITEMS } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { Button, Modal } from '../ds/index.js';
import { SceneWelcome, ScenePlace, SceneCustomize, SceneTips } from './DeskTutorialScenes.js';

/** Identifiant et rôle lus dans le JWT (affichage seulement). */
function jwtInfo(jwt: string | null): { sub: string; role: string } | null {
  try {
    const p = JSON.parse(atob(jwt!.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string; role?: string };
    return p.sub && p.role ? { sub: p.sub, role: p.role } : null;
  } catch {
    return null;
  }
}

/** Vu une fois par compte et par campus, dans ce navigateur (une préférence, pas une donnée à garantir). */
const seenKey = (userId: string) => `placio:desk-tutorial:${getTenantSlug()}:${userId}`;
function hasSeen(userId: string): boolean {
  try { return localStorage.getItem(seenKey(userId)) === '1'; } catch { return false; }
}
function markSeen(userId: string): void {
  try { localStorage.setItem(seenKey(userId), '1'); } catch { /* navigation privée : il réapparaîtra */ }
}

const STEPS: Array<{ title: string; scene: ReactNode; body: ReactNode }> = [
  {
    title: 'Ton bureau, à ton image',
    scene: <SceneWelcome />,
    body: (
      <>
        <p>Dans cet espace, chaque membre prend <strong>son propre bureau</strong> et l&apos;aménage comme il le souhaite.</p>
        <p>Tu as droit à <strong>un seul bureau</strong> sur tout le campus. Les autres le voient à ton nom, et tu es prévenu quand quelqu&apos;un passe te voir.</p>
      </>
    ),
  },
  {
    title: 'Choisir ton emplacement',
    scene: <ScenePlace />,
    body: (
      <ol>
        <li>Les emplacements <strong>libres</strong> sont tracés au sol, avec la mention <strong>« Libre »</strong>.</li>
        <li>Place-toi sur celui qui te plaît et clique sur <strong>« Prendre ce bureau »</strong>.</li>
        <li>Il est à toi : les autres le voient désormais à ton nom.</li>
      </ol>
    ),
  },
  {
    title: 'L’aménager',
    scene: <SceneCustomize />,
    body: (
      <ol>
        <li>Place-toi sur ton bureau et clique sur <strong>« Personnaliser »</strong>.</li>
        <li>Choisis un objet dans le panneau, puis clique sur ton bureau pour le poser. Jusqu&apos;à {MAX_DESK_ITEMS} objets.</li>
        <li>Clique sur un objet posé pour le déplacer ; <strong>Suppr</strong> pour le retirer.</li>
        <li>Les écrans, lampes et plantes se posent directement sur la table. Clique sur <strong>« Terminer »</strong> pour enregistrer.</li>
      </ol>
    ),
  },
  {
    title: 'Quelques conseils',
    scene: <SceneTips />,
    body: (
      <ul>
        <li><strong>Garde une rangée derrière ta table</strong> pour ton fauteuil : c&apos;est là que tu t&apos;installes.</li>
        <li>Les objets <strong>bloquent le passage</strong>, sauf les sièges et les tapis : vérifie que tu peux encore entrer dans ton bureau.</li>
        <li>Envie de changer de place ? <strong>« Libérer »</strong> efface ton aménagement ; tu pourras ensuite prendre un autre emplacement libre.</li>
      </ul>
    ),
  },
];

/**
 * DeskTutorial — présentation des bureaux personnels, ouverte d'office à la
 * première entrée d'un membre dans un espace bureaux, et à la demande
 * (`ui:desk-tutorial-open`, bouton « ? » de PersonalDeskPrompt).
 */
export function DeskTutorial() {
  const area = useGameStore((s) => s.personalDeskUi.area);
  const jwt = useJwt();
  const info = jwtInfo(jwt);
  const isMember = !!info && ['OWNER', 'ADMIN', 'MEMBER'].includes(info.role);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Première entrée dans un espace bureaux (membres seulement : les invités n'ont pas de bureau).
  useEffect(() => {
    if (area && isMember && info && !hasSeen(info.sub)) {
      markSeen(info.sub);
      setStep(0);
      setOpen(true);
    }
  }, [area?.id, isMember]);

  useEffect(() => {
    const onOpen = () => { setStep(0); setOpen(true); };
    EventBus.on('ui:desk-tutorial-open', onOpen);
    return () => { EventBus.off('ui:desk-tutorial-open', onOpen); };
  }, []);

  // Clavier rendu à la fenêtre pendant la lecture (Échap, Entrée).
  useEffect(() => {
    if (!open) return;
    EventBus.emit('ui:modal-opened', {});
    return () => { EventBus.emit('ui:modal-closed', {}); };
  }, [open]);

  if (!open) return null;
  const current = STEPS[step]!;
  const last = step === STEPS.length - 1;
  const close = () => setOpen(false);

  return (
    <Modal
      title={current.title}
      glass
      onClose={close}
      footer={
        <>
          <span className="pl-muted" style={{ marginRight: 'auto', fontSize: 12 }}>{step + 1} / {STEPS.length}</span>
          {step > 0 ? <Button variant="ghost" onClick={() => setStep(step - 1)}>Précédent</Button> : <Button variant="ghost" onClick={close}>Passer</Button>}
          <Button variant="primary" onClick={() => (last ? close() : setStep(step + 1))}>{last ? 'C’est parti' : 'Suivant'}</Button>
        </>
      }
    >
      <div className="pl-desk-tuto" style={{ color: 'var(--color-text-primary)', lineHeight: 1.55 }}>
        {current.scene}
        <div>{current.body}</div>
      </div>
    </Modal>
  );
}
